import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import { motion } from "framer-motion";
import { FadeUp, Stagger, item } from "@/components/Reveal";

export function SectionHeader({ eyebrow, title, text, cta, to }) {
  return (
    <div className="grid grid-cols-12 gap-6">
      <FadeUp className="col-span-12 lg:col-span-7">
        {eyebrow && <div className="earmark mb-5">{eyebrow}</div>}
        <h2 className="h-section text-3xl sm:text-4xl lg:text-[3.25rem] text-black">{title}</h2>
      </FadeUp>
      <FadeUp delay={0.15} className="col-span-12 lg:col-span-5 lg:pl-8 lg:pt-10">
        {text && <p className="text-sm md:text-base leading-relaxed text-[var(--ink-2)]">{text}</p>}
        {cta && to && (
          <Link to={to} data-testid={`section-cta-${to.replace(/\//g, "")}`} className="link-arrow mt-6">{cta} <ArrowRight size={16} /></Link>
        )}
      </FadeUp>
    </div>
  );
}

export function PhotoCard({ to, image, title, text, cta, tall = false, testid }) {
  return (
    <motion.div variants={item} className="group">
      <Link to={to} data-testid={testid} className="block">
        <div className={`img-frame ${tall ? "aspect-[4/5]" : "aspect-[16/11]"}`}>
          <img src={image} alt={title} loading="lazy" />
          <span className="absolute right-4 top-4 flex h-9 w-9 items-center justify-center bg-white text-black opacity-0 translate-y-1 transition-all duration-500 group-hover:opacity-100 group-hover:translate-y-0">
            <ArrowUpRight size={15} />
          </span>
        </div>
        <h3 data-testid={`${testid}-title`} className="font-head mt-5 text-xl font-medium tracking-tight text-black">{title}</h3>
        {text && <p data-testid={`${testid}-description`} className="mt-2 text-[13.5px] leading-relaxed text-[var(--ink-2)]">{text}</p>}
        {cta && <span data-testid={`${testid}-cta`} className="link-arrow mt-4 text-[13px]">{cta} <ArrowRight size={14} /></span>}
      </Link>
    </motion.div>
  );
}

export function IndexRow({ to, title, text, testid, image, cta }) {
  return (
    <motion.div variants={item}>
      <Link to={to} data-testid={testid} className="group grid grid-cols-12 gap-5 border-t border-[var(--line)] py-7 transition-colors duration-300 hover:border-black md:py-8">
        <div className="col-span-12 md:col-span-4 flex items-start gap-5">
          {image && <span className="img-frame hidden md:block h-16 w-24 flex-none"><img src={image} alt="" loading="lazy" /></span>}
          <h3 data-testid={`${testid}-title`} className="font-head text-lg md:text-xl font-medium tracking-tight text-black">{title}</h3>
        </div>
        <p data-testid={`${testid}-description`} className="col-span-12 md:col-span-6 text-[13.5px] leading-relaxed text-[var(--ink-2)]">{text}</p>
        <span data-testid={`${testid}-cta`} className="col-span-12 md:col-span-2 flex md:justify-end items-start text-[13px] font-medium text-black gap-2">
          {cta} <ArrowRight size={16} className="transition-transform duration-500 group-hover:translate-x-1" />
        </span>
      </Link>
    </motion.div>
  );
}

export function CardGrid({ children, cols = "md:grid-cols-2 lg:grid-cols-3", className = "" }) {
  return <Stagger className={`grid grid-cols-1 gap-x-8 gap-y-12 ${cols} ${className}`}>{children}</Stagger>;
}
